import React, { useState, useEffect, useRef, useCallback } from 'react';
import { assetAPI, taskAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Upload, File, Image, Video, Music, FileText, Archive, PenTool,
  Download, Trash2, Edit2, X, Check, ChevronDown, Search,
  Grid, List, Eye, ExternalLink, Clock, User, Tag, Link2, 
  AlertCircle, CheckCircle, Folder, Filter, MoreVertical
} from 'lucide-react';

// File type icons mapping
const FILE_ICONS = {
  image: Image,
  video: Video,
  audio: Music,
  document: FileText,
  design: PenTool,
  archive: Archive,
  other: File
};

// Status colors
const STATUS_COLORS = {
  draft: { bg: '#F3F4F6', text: '#6B7280', label: 'Draft' },
  final: { bg: '#D1FAE5', text: '#059669', label: 'Final' },
  archived: { bg: '#FEE2E2', text: '#DC2626', label: 'Archived' }
};

// Format relative time
const formatRelativeTime = (date) => {
  if (!date) return 'Unknown';
  const now = new Date();
  const d = new Date(date);
  const diff = now - d;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Asset Card Component
const AssetCard = ({ asset, onPreview, onEdit, onDelete, viewMode = 'grid' }) => {
  const Icon = FILE_ICONS[asset.file_type] || File;
  const statusColor = STATUS_COLORS[asset.status] || STATUS_COLORS.draft;
  
  const isGrid = viewMode === 'grid';
  
  return (
    <div 
      className={`bg-white rounded-lg border hover:shadow-md transition-all cursor-pointer group ${isGrid ? 'p-4' : 'p-3 flex items-center gap-4'}`}
      style={{ borderColor: '#E9ECEF' }}
      data-testid={`asset-card-${asset.id}`}
    >
      {/* Thumbnail/Icon */}
      <div 
        className={`${isGrid ? 'mb-3 aspect-video' : 'w-12 h-12 flex-shrink-0'} rounded-lg flex items-center justify-center overflow-hidden`}
        style={{ backgroundColor: '#F3F4F6' }}
        onClick={() => asset.is_previewable && onPreview(asset)}
      >
        {asset.is_previewable && asset.file_type === 'image' ? (
          <img 
            src={asset.download_url} 
            alt={asset.file_name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <Icon 
            className={`${isGrid ? 'w-10 h-10' : 'w-6 h-6'}`} 
            style={{ color: '#6B7280' }} 
          />
        )}
      </div>
      
      {/* Content */}
      <div className={`flex-1 ${isGrid ? '' : 'min-w-0'}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p 
              className={`font-medium truncate ${isGrid ? 'text-sm' : 'text-sm'}`} 
              style={{ color: '#264653' }}
              title={asset.file_name}
            >
              {asset.file_name}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs" style={{ color: '#9CA3AF' }}>
                {asset.file_size_formatted}
              </span>
              {asset.version_number > 1 && (
                <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>
                  v{asset.version_number}
                </span>
              )}
            </div>
          </div>
          
          {/* Deliverable badge */}
          {asset.is_deliverable && (
            <span 
              className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
            >
              Deliverable
            </span>
          )}
        </div>
        
        {/* Status & Meta */}
        <div className={`flex items-center gap-3 ${isGrid ? 'mt-3' : 'mt-1'}`}>
          <span 
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
          >
            {statusColor.label}
          </span>
          
          {isGrid && (
            <div className="flex items-center gap-1.5 text-xs" style={{ color: '#9CA3AF' }}>
              <div 
                className="w-5 h-5 rounded-full flex items-center justify-center text-xs"
                style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F', fontSize: '9px' }}
              >
                {asset.uploaded_by_avatar}
              </div>
              <span>{formatRelativeTime(asset.uploaded_at)}</span>
            </div>
          )}
        </div>
        
        {/* Linked task */}
        {asset.linked_task_name && isGrid && (
          <div className="flex items-center gap-1.5 mt-2 text-xs" style={{ color: '#6B7280' }}>
            <Link2 className="w-3 h-3" />
            <span className="truncate">{asset.linked_task_name}</span>
          </div>
        )}
      </div>
      
      {/* Actions */}
      <div className={`${isGrid ? 'flex items-center gap-1 mt-3 pt-3 border-t' : 'flex items-center gap-1 flex-shrink-0'}`} style={{ borderColor: '#E9ECEF' }}>
        {asset.is_previewable && (
          <button 
            onClick={(e) => { e.stopPropagation(); onPreview(asset); }}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
            title="Preview"
          >
            <Eye className="w-4 h-4" style={{ color: '#6B7280' }} />
          </button>
        )}
        <a 
          href={asset.download_url}
          download={asset.file_name}
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" style={{ color: '#6B7280' }} />
        </a>
        <button 
          onClick={(e) => { e.stopPropagation(); onEdit(asset); }}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          title="Edit"
        >
          <Edit2 className="w-4 h-4" style={{ color: '#6B7280' }} />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(asset); }}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} />
        </button>
      </div>
    </div>
  );
};

// Asset Preview Modal
const AssetPreviewModal = ({ asset, onClose }) => {
  if (!asset) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="asset-preview-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#E9ECEF' }}>
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold" style={{ color: '#264653' }}>{asset.file_name}</h3>
            {asset.is_deliverable && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                Deliverable
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a 
              href={asset.download_url}
              download={asset.file_name}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100"
              style={{ color: '#2A9D8F' }}
            >
              <Download className="w-4 h-4" />
              Download
            </a>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" style={{ color: '#6B7280' }} />
            </button>
          </div>
        </div>
        
        {/* Preview Content */}
        <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          {asset.file_type === 'image' && (
            <img 
              src={asset.download_url} 
              alt={asset.file_name}
              className="max-w-full h-auto mx-auto rounded-lg"
            />
          )}
          {asset.file_type === 'video' && (
            <video 
              src={asset.download_url}
              controls
              className="max-w-full h-auto mx-auto rounded-lg"
            >
              Your browser does not support video playback.
            </video>
          )}
          {asset.file_type === 'audio' && (
            <div className="flex flex-col items-center py-8">
              <Music className="w-16 h-16 mb-4" style={{ color: '#9CA3AF' }} />
              <audio 
                src={asset.download_url}
                controls
                className="w-full max-w-md"
              >
                Your browser does not support audio playback.
              </audio>
            </div>
          )}
          {asset.file_type === 'document' && asset.file_extension === 'pdf' && (
            <iframe 
              src={asset.download_url}
              className="w-full rounded-lg"
              style={{ height: '70vh' }}
              title={asset.file_name}
            />
          )}
        </div>
        
        {/* Footer Meta */}
        <div className="flex items-center gap-4 px-4 py-3 border-t text-xs" style={{ borderColor: '#E9ECEF', color: '#6B7280' }}>
          <span>Size: {asset.file_size_formatted}</span>
          <span>Version: {asset.version_number}</span>
          <span>Uploaded: {new Date(asset.uploaded_at).toLocaleDateString()}</span>
          <span>By: {asset.uploaded_by_name}</span>
        </div>
      </div>
    </div>
  );
};

// Edit Asset Modal
const EditAssetModal = ({ asset, tasks, onSave, onClose }) => {
  const [isDeliverable, setIsDeliverable] = useState(asset?.is_deliverable || false);
  const [status, setStatus] = useState(asset?.status || 'draft');
  const [linkedTaskId, setLinkedTaskId] = useState(asset?.linked_task_id || '');
  const [linkedLane, setLinkedLane] = useState(asset?.linked_lane || '');
  const [notes, setNotes] = useState(asset?.notes || '');
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        is_deliverable: isDeliverable,
        status,
        linked_task_id: linkedTaskId || null,
        linked_lane: linkedLane || null,
        notes: notes || null
      });
      onClose();
    } catch (error) {
      console.error('Error saving asset:', error);
    } finally {
      setSaving(false);
    }
  };
  
  if (!asset) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-asset-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#E9ECEF' }}>
          <h3 className="text-lg font-semibold" style={{ color: '#264653' }}>Edit Asset</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" style={{ color: '#6B7280' }} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* File Name (read-only) */}
          <div>
            <label className="text-xs font-medium" style={{ color: '#6B7280' }}>File Name</label>
            <p className="text-sm mt-1" style={{ color: '#264653' }}>{asset.file_name}</p>
          </div>
          
          {/* Deliverable Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium" style={{ color: '#264653' }}>Mark as Deliverable</label>
            <button 
              onClick={() => setIsDeliverable(!isDeliverable)}
              className={`w-12 h-6 rounded-full transition-colors ${isDeliverable ? 'bg-amber-400' : 'bg-gray-200'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${isDeliverable ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          
          {/* Status */}
          <div>
            <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Status</label>
            <select 
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              style={{ borderColor: '#E9ECEF' }}
            >
              <option value="draft">Draft</option>
              <option value="final">Final</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          
          {/* Linked Task */}
          <div>
            <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Link to Task</label>
            <select 
              value={linkedTaskId}
              onChange={(e) => setLinkedTaskId(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              style={{ borderColor: '#E9ECEF' }}
            >
              <option value="">No linked task</option>
              {tasks.map(task => (
                <option key={task.id} value={task.id}>{task.title}</option>
              ))}
            </select>
          </div>
          
          {/* Notes */}
          <div>
            <label className="text-xs font-medium" style={{ color: '#6B7280' }}>Notes</label>
            <textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this asset..."
              rows={3}
              className="w-full mt-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              style={{ borderColor: '#E9ECEF' }}
            />
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-2 p-4 border-t" style={{ borderColor: '#E9ECEF' }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#2A9D8F', color: 'white' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Version Conflict Modal
const VersionConflictModal = ({ filename, currentVersion, onNewVersion, onReplace, onCancel }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="version-conflict-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="p-4 border-b" style={{ borderColor: '#E9ECEF' }}>
          <div className="flex items-center gap-2 text-amber-600">
            <AlertCircle className="w-5 h-5" />
            <h3 className="font-semibold">File Already Exists</h3>
          </div>
        </div>
        
        <div className="p-4">
          <p className="text-sm" style={{ color: '#6B7280' }}>
            A file named <span className="font-medium text-gray-900">"{filename}"</span> already exists in this project (v{currentVersion}).
          </p>
          <p className="text-sm mt-2" style={{ color: '#6B7280' }}>
            What would you like to do?
          </p>
        </div>
        
        <div className="p-4 space-y-2">
          <button 
            onClick={onNewVersion}
            className="w-full flex items-center gap-3 p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors"
            style={{ borderColor: '#E9ECEF' }}
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D1FAE5' }}>
              <CheckCircle className="w-4 h-4" style={{ color: '#059669' }} />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: '#264653' }}>Upload as New Version</p>
              <p className="text-xs" style={{ color: '#9CA3AF' }}>Create v{currentVersion + 1}, keep history</p>
            </div>
          </button>
          
          <button 
            onClick={onCancel}
            className="w-full text-sm py-2 text-center hover:underline"
            style={{ color: '#6B7280' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Assets Tab Component
const AssetsTab = ({ projectId, tasks = [] }) => {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // UI State
  const [viewMode, setViewMode] = useState('grid');
  const [groupBy, setGroupBy] = useState('none');
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  
  // Modal State
  const [previewAsset, setPreviewAsset] = useState(null);
  const [editAsset, setEditAsset] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [versionConflict, setVersionConflict] = useState(null);
  
  const fileInputRef = useRef(null);
  const pendingFileRef = useRef(null);
  
  // Fetch assets
  const fetchAssets = useCallback(async () => {
    try {
      const [assetsRes, summaryRes] = await Promise.all([
        assetAPI.getProjectAssets(projectId, { ...filters, search: search || undefined }),
        assetAPI.getProjectAssetSummary(projectId)
      ]);
      setAssets(assetsRes.data.assets || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, filters, search]);
  
  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check for existing file with same name
    try {
      const checkRes = await assetAPI.checkVersion(projectId, file.name);
      if (checkRes.data.exists) {
        pendingFileRef.current = file;
        setVersionConflict({
          filename: file.name,
          currentVersion: checkRes.data.current_version
        });
        return;
      }
      
      // No conflict, upload directly
      await uploadFile(file, false);
    } catch (error) {
      console.error('Error checking version:', error);
      await uploadFile(file, false);
    }
  };
  
  // Upload file
  const uploadFile = async (file, isNewVersion = false) => {
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_deliverable', 'false');
    formData.append('status', 'draft');
    formData.append('is_new_version', isNewVersion ? 'true' : 'false');
    
    try {
      // Simulate progress (actual progress tracking would need axios config)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);
      
      await assetAPI.uploadAsset(projectId, formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Refresh assets
      await fetchAssets();
      
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploading(false);
      setUploadProgress(0);
    }
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    pendingFileRef.current = null;
    setVersionConflict(null);
  };
  
  // Handle version conflict
  const handleNewVersion = () => {
    if (pendingFileRef.current) {
      uploadFile(pendingFileRef.current, true);
    }
  };
  
  // Handle edit save
  const handleEditSave = async (updates) => {
    if (!editAsset) return;
    
    await assetAPI.updateAsset(editAsset.id, updates);
    await fetchAssets();
    setEditAsset(null);
  };
  
  // Handle delete
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    await assetAPI.deleteAsset(deleteConfirm.id);
    await fetchAssets();
    setDeleteConfirm(null);
  };
  
  // Group assets
  const groupedAssets = React.useMemo(() => {
    if (groupBy === 'none') return { 'All Assets': assets };
    
    const groups = {};
    assets.forEach(asset => {
      let key;
      switch (groupBy) {
        case 'type':
          key = asset.file_type.charAt(0).toUpperCase() + asset.file_type.slice(1);
          break;
        case 'status':
          key = STATUS_COLORS[asset.status]?.label || 'Unknown';
          break;
        case 'deliverable':
          key = asset.is_deliverable ? 'Deliverables' : 'Supporting Assets';
          break;
        case 'task':
          key = asset.linked_task_name || 'Unlinked';
          break;
        default:
          key = 'All';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(asset);
    });
    return groups;
  }, [assets, groupBy]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p style={{ color: '#8D99AE' }}>Loading assets...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="assets-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
          <input 
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            style={{ borderColor: '#E9ECEF' }}
          />
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Group By */}
          <select 
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            style={{ borderColor: '#E9ECEF' }}
          >
            <option value="none">No grouping</option>
            <option value="type">Group by Type</option>
            <option value="status">Group by Status</option>
            <option value="deliverable">Group by Deliverable</option>
            <option value="task">Group by Task</option>
          </select>
          
          {/* Filter: Deliverables Only */}
          <button 
            onClick={() => setFilters(prev => ({ 
              ...prev, 
              is_deliverable: prev.is_deliverable ? undefined : true 
            }))}
            className={`px-3 py-2 text-sm border rounded-lg transition-colors ${filters.is_deliverable ? 'bg-amber-50 border-amber-200' : ''}`}
            style={{ borderColor: filters.is_deliverable ? '#FCD34D' : '#E9ECEF' }}
          >
            <Tag className="w-4 h-4 inline mr-1" />
            Deliverables
          </button>
          
          {/* View Mode Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden" style={{ borderColor: '#E9ECEF' }}>
            <button 
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : ''}`}
            >
              <Grid className="w-4 h-4" style={{ color: viewMode === 'grid' ? '#2A9D8F' : '#6B7280' }} />
            </button>
            <button 
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
            >
              <List className="w-4 h-4" style={{ color: viewMode === 'list' ? '#2A9D8F' : '#6B7280' }} />
            </button>
          </div>
          
          {/* Upload Button */}
          <input 
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-2"
            style={{ backgroundColor: '#2A9D8F', color: 'white' }}
          >
            <Upload className="w-4 h-4" />
            Upload
          </Button>
        </div>
      </div>
      
      {/* Upload Progress */}
      {uploading && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-teal-700">Uploading...</span>
            <span className="text-sm text-teal-600">{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}
      
      {/* Empty State */}
      {assets.length === 0 && !uploading && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: '#F3F4F6' }}
          >
            <Folder className="w-8 h-8" style={{ color: '#9CA3AF' }} />
          </div>
          <h3 className="text-lg font-medium mb-1" style={{ color: '#264653' }}>No assets yet</h3>
          <p className="text-sm mb-4" style={{ color: '#8D99AE' }}>
            Upload files to start building your project deliverables
          </p>
          <Button 
            onClick={() => fileInputRef.current?.click()}
            className="gap-2"
            style={{ backgroundColor: '#2A9D8F', color: 'white' }}
          >
            <Upload className="w-4 h-4" />
            Upload First Asset
          </Button>
        </div>
      )}
      
      {/* Asset Groups */}
      {Object.entries(groupedAssets).map(([groupName, groupAssets]) => (
        <div key={groupName}>
          {groupBy !== 'none' && (
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2" style={{ color: '#6B7280' }}>
              <Folder className="w-4 h-4" />
              {groupName}
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F3F4F6' }}>
                {groupAssets.length}
              </span>
            </h4>
          )}
          
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
            : 'space-y-2'
          }>
            {groupAssets.map(asset => (
              <AssetCard 
                key={asset.id}
                asset={asset}
                viewMode={viewMode}
                onPreview={setPreviewAsset}
                onEdit={setEditAsset}
                onDelete={setDeleteConfirm}
              />
            ))}
          </div>
        </div>
      ))}
      
      {/* Preview Modal */}
      {previewAsset && (
        <AssetPreviewModal 
          asset={previewAsset}
          onClose={() => setPreviewAsset(null)}
        />
      )}
      
      {/* Edit Modal */}
      {editAsset && (
        <EditAssetModal 
          asset={editAsset}
          tasks={tasks}
          onSave={handleEditSave}
          onClose={() => setEditAsset(null)}
        />
      )}
      
      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
              <Trash2 className="w-6 h-6" style={{ color: '#EF4444' }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#264653' }}>Delete Asset?</h3>
            <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
              Are you sure you want to delete "{deleteConfirm.file_name}"? This action cannot be undone.
            </p>
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button 
                onClick={handleDelete}
                style={{ backgroundColor: '#EF4444', color: 'white' }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Version Conflict Modal */}
      {versionConflict && (
        <VersionConflictModal 
          filename={versionConflict.filename}
          currentVersion={versionConflict.currentVersion}
          onNewVersion={handleNewVersion}
          onCancel={() => {
            setVersionConflict(null);
            pendingFileRef.current = null;
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
      )}
    </div>
  );
};

export default AssetsTab;
